/**
 * O contrato vinculado ao card da legalização: em que estado ele está e o que o card
 * oferece em cada um (decisão de 30/09 — casos de uso do card com o contrato).
 *
 *  - sem      → "Gerar ..." e "Vincular contrato existente"
 *  - edicao   → "Continuar contrato #N" (o gerador retoma de onde parou)
 *  - pronto   → "Abrir contrato #N" (listagem, com o contrato em destaque)
 *  - fora     → arquivado ou cancelado: aviso; trocar ou desvincular
 *  - sumiu    → o vinculado não existe mais (rascunho descartado): aviso; gerar ou vincular
 *  - erro     → a leitura falhou por outro motivo (500, rede): aviso; abrir e tentar de novo
 *  - carregando → vinculado, com a leitura em voo
 */
import type {
  StatusContratoSocietario,
  TipoOperacaoSocietario,
} from '../../../hooks/useSocietario';
import type { TipoLegalizacao } from '../../../hooks/useLegalizacao';

export type EstadoDoContrato =
  | 'sem'
  | 'edicao'
  | 'pronto'
  | 'fora'
  | 'sumiu'
  | 'erro'
  | 'carregando';

// Em edição no gerador: rascunhos e o "em revisão" (editado depois de gerar).
const STATUS_EM_EDICAO: readonly string[] = [
  'rascunho_inicial',
  'rascunho',
  'campos_pendentes',
  'em_revisao',
];
const STATUS_PRONTO: readonly string[] = ['gerado', 'assinado'];

export const ROTULO_DO_STATUS: Record<StatusContratoSocietario, string> = {
  rascunho_inicial: 'rascunho',
  rascunho: 'rascunho',
  campos_pendentes: 'campos pendentes',
  em_revisao: 'em revisão',
  gerado: 'gerado',
  assinado: 'assinado',
  cancelado: 'cancelado',
  arquivado: 'arquivado',
};

export const rotuloDoStatus = (status: string | null | undefined): string =>
  ROTULO_DO_STATUS[status as StatusContratoSocietario] ?? status ?? '';

export function estadoDoContrato(
  contratoId: number | null | undefined,
  leitura: { status?: string | null } | null | undefined,
  naoEncontrado: boolean,
  falhouLeitura = false,
): EstadoDoContrato {
  if (!contratoId) return 'sem';
  if (naoEncontrado) return 'sumiu';
  if (!leitura) return falhouLeitura ? 'erro' : 'carregando';
  const status = leitura.status ?? '';
  if (STATUS_EM_EDICAO.includes(status)) return 'edicao';
  if (STATUS_PRONTO.includes(status)) return 'pronto';
  return 'fora';
}

/** Tipos de contrato que cada tipo de processo aceita (o back confere o mesmo). */
export const CONTRATOS_DO_PROCESSO: Record<TipoLegalizacao, TipoOperacaoSocietario[]> = {
  constituicao: ['constituicao'],
  // A transformação (EI/MEI → LTDA) é registrada na junta como alteração.
  alteracao: ['alteracao', 'transformacao'],
  baixa: [],
};

export const urlParaContinuar = (contratoId: number, legalizacaoId: number): string =>
  `/societario/contratos/criar?contratoId=${contratoId}&legalizacaoId=${legalizacaoId}`;

export const urlDaListagem = (contratoId: number): string =>
  `/societario/contratos?destaque=${contratoId}`;

/** Busca inicial da lista de contratos: o CNPJ (alteração) ou o nome do card. */
export const buscaInicial = (cnpj: string | null | undefined, titulo: string | null | undefined) =>
  (cnpj ?? '').replace(/\D/g, '') || (titulo ?? '').trim();
